import apiClient from './client';

export interface DocumentStats {
  total: number;
  indexed: number;
  pending: number;
  failed: number;
  totalSizeBytes: number;
  uniqueTopics: number;
}

export interface KnowledgeBaseStats {
  totalChunks: number;
  documentsWithChunks: number;
}

export interface QueryStats {
  total: number;
  today: number;
  thisWeek: number;
  thisMonth: number;
  avgResponseTime: number;
  successRate: number;
}

export interface FeedbackStats {
  total: number;
  helpful: number;
  notHelpful: number;
  incorrect: number;
  incomplete: number;
  satisfactionRate: number | null;
}

export interface UserStats {
  total: number;
  active: number;
}

export interface DepartmentStats {
  total: number;
}

export interface QueryTrendItem {
  date: string;
  queries: number;
  avgResponseTime: number;
}

export interface TopicItem {
  topic: string;
  count: number;
}

export interface TopDocumentItem {
  id: string;
  filename: string;
  topic: string | null;
  accessCount: number;
  avgSimilarity: number;
}

export interface FeedbackDistributionItem {
  name: string;
  value: number;
  color: string;
}

export interface RecentQueryItem {
  id: string;
  queryText: string;
  topic: string | null;
  responseTime: number;
  success: boolean;
  createdAt: string;
  feedback: string | null;
  docsUsed: number;
}

export interface DashboardStats {
  stats: {
    documents: DocumentStats;
    knowledgeBase: KnowledgeBaseStats;
    queries: QueryStats;
    feedback: FeedbackStats;
    users: UserStats;
    departments: DepartmentStats;
  };
  charts: {
    queryTrend: QueryTrendItem[];
    topTopics: TopicItem[];
    topDocuments: TopDocumentItem[];
    feedbackDistribution: FeedbackDistributionItem[];
  };
  recentQueries: RecentQueryItem[];
}

export interface DocumentTopicItem {
  topic: string;
  documentCount: number;
  totalSize: number;
  indexedCount: number;
}

export interface QueryAnalyticsItem {
  date: string;
  totalQueries: number;
  uniqueUsers: number;
  avgResponseTime: number;
  successRate: number;
}

// Dashboard API methods
export const dashboardApi = {
  /**
   * Get comprehensive dashboard statistics
   */
  getStats: async (): Promise<DashboardStats> => {
    const response = await apiClient.get<{ success: boolean } & DashboardStats>('/dashboard/stats');
    return response.data;
  },

  /**
   * Get document topics breakdown
   */
  getDocumentTopics: async (): Promise<DocumentTopicItem[]> => {
    const response = await apiClient.get<{ success: boolean; topics: DocumentTopicItem[] }>('/dashboard/topics');
    return response.data.topics;
  },

  /**
   * Get query analytics over time
   */
  getQueryAnalytics: async (days: number = 30): Promise<QueryAnalyticsItem[]> => {
    const response = await apiClient.get<{ success: boolean; analytics: QueryAnalyticsItem[] }>(
      `/dashboard/query-analytics?days=${days}`
    );
    return response.data.analytics;
  },
};

export default dashboardApi;
